import React, { useState, useRef, useEffect } from "react";
import { X, Plus, GripHorizontal, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Define the Tab type
interface Tab {
  id: string;
  title: string | JSX.Element;
  content: React.ReactNode;
}

interface BrowserTabsProps {
  initialTabs?: Tab[];
  defaultTabId?: string;
  onTabChange?: (tabId: string) => void;
  onTabClose?: (tabId: string) => void;
  onTabAdd?: () => void;
  onTabRename?: (tabId: string, newTitle: string) => void;
  onTabsReorder?: (newTabs: Tab[]) => void;
  isAddingTab?: boolean;
}

export const BrowserTabs: React.FC<BrowserTabsProps> = ({
  initialTabs = [],
  defaultTabId,
  onTabChange,
  onTabClose,
  onTabAdd,
  onTabRename,
  onTabsReorder,
  isAddingTab,
}) => {
  // State for tabs and active tab
  const [tabs, setTabs] = useState<Tab[]>(initialTabs);
  const [activeTabId, setActiveTabId] = useState<string>(
    defaultTabId || (initialTabs.length > 0 ? initialTabs[0].id : "")
  );
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);
  const [removingTabId, setRemovingTabId] = useState<string | null>(null);
  const lastUpdateRef = useRef<number>(Date.now());

  // Reference to the tabs container for scrolling
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingTabId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTabId]);

  // Update tabs when initialTabs changes
  useEffect(() => {
    const now = Date.now();
    // Only update if it's been at least 50ms since the last update to avoid rapid flickering
    if (now - lastUpdateRef.current > 50) {
      lastUpdateRef.current = now;

      // Check if we need to update the tabs
      if (
        initialTabs.length !== tabs.length ||
        JSON.stringify(initialTabs.map((t) => t.id)) !==
          JSON.stringify(tabs.map((t) => t.id))
      ) {
        // Important: preserve the exact order of tabs as provided in initialTabs
        setTabs([...initialTabs]);

        // If no active tab or active tab is not in the new tabs, set the default or first one as active
        if (
          !activeTabId ||
          !initialTabs.some((tab) => tab.id === activeTabId)
        ) {
          // Prioritize defaultTabId if it exists in the tabs
          let newActiveTabId = "";
          if (
            defaultTabId &&
            initialTabs.some((tab) => tab.id === defaultTabId)
          ) {
            newActiveTabId = defaultTabId;
          } else {
            newActiveTabId = initialTabs[0]?.id || "";
          }

          if (newActiveTabId) {
            setActiveTabId(newActiveTabId);
            onTabChange?.(newActiveTabId);
          }
        }
      }
    }
  }, [initialTabs, activeTabId, onTabChange, tabs, defaultTabId]);

  // Update active tab when defaultTabId changes
  useEffect(() => {
    if (
      defaultTabId &&
      defaultTabId !== activeTabId &&
      tabs.some((tab) => tab.id === defaultTabId)
    ) {
      setActiveTabId(defaultTabId);
      onTabChange?.(defaultTabId);
    }
  }, [defaultTabId, activeTabId, tabs, onTabChange]);

  // Scroll to active tab when it changes
  useEffect(() => {
    if (activeTabId && tabsContainerRef.current) {
      const tabElement = document.querySelector(
        `[data-tab-id="${activeTabId}"]`
      ) as HTMLElement;
      if (tabElement) {
        // Calculate the tab's position relative to the container
        const container = tabsContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const tabRect = tabElement.getBoundingClientRect();

        // Calculate if the tab is outside the visible area
        const isTabLeftOfView = tabRect.left < containerRect.left;
        const isTabRightOfView = tabRect.right > containerRect.right;

        if (isTabLeftOfView) {
          // Scroll to make the tab visible on the left edge
          container.scrollLeft += tabRect.left - containerRect.left - 8; // Add a small buffer
        } else if (isTabRightOfView) {
          // Scroll to make the tab visible on the right edge
          container.scrollLeft += tabRect.right - containerRect.right + 8; // Add a small buffer
        }
      }
    }
  }, [activeTabId, tabs]);

  // Handler to change the active tab
  const handleTabClick = (tabId: string) => {
    if (editingTabId) return;
    if (removingTabId === tabId) return; // Don't allow clicking on a tab being removed

    setActiveTabId(tabId);
    onTabChange?.(tabId);
  };

  // Handler to start editing tab title
  const handleDoubleClick = (tab: Tab) => {
    setEditingTabId(tab.id);

    // Extract current name for editing
    let currentName = "";
    if (typeof tab.title === "string") {
      currentName = tab.title;
    } else {
      // For JSX titles, try to extract the text from the span element
      // This assumes the tab title structure is consistent with CartButton implementation
      const titleElement = tab.title as React.ReactElement;
      if (titleElement.props && titleElement.props.children) {
        // Look for the span with the name
        const children = titleElement.props.children;
        if (Array.isArray(children)) {
          const nameSpan = children.find(
            (child) => typeof child === "object" && child.type === "span"
          );
          if (nameSpan && nameSpan.props && nameSpan.props.children) {
            currentName = nameSpan.props.children;
          }
        }
      }
    }

    setEditingTitle(currentName || "Cart");
  };

  // Handler to save edited tab title
  const handleTitleSubmit = (tabId: string) => {
    if (!editingTitle.trim()) return;

    const newTabs = tabs.map((tab) =>
      tab.id === tabId ? { ...tab, title: editingTitle.trim() } : tab
    );
    setTabs(newTabs);
    setEditingTabId(null);
    onTabRename?.(tabId, editingTitle.trim());

    // Notify parent of tab reordering
    onTabsReorder?.(newTabs);
  };

  // Handler to handle key press in edit mode
  const handleKeyDown = (e: React.KeyboardEvent, tabId: string) => {
    if (e.key === "Enter") {
      handleTitleSubmit(tabId);
    } else if (e.key === "Escape") {
      setEditingTabId(null);
    }
  };

  // Handler to close a tab
  const handleTabClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any default behavior

    // Set this tab as being removed for visual feedback
    setRemovingTabId(tabId);

    // Immediately remove tab from UI for better visual feedback
    const updatedTabs = tabs.filter((tab) => tab.id !== tabId);
    setTabs(updatedTabs);

    // If the closed tab was active, activate another tab
    if (activeTabId === tabId && updatedTabs.length > 0) {
      const closingIndex = tabs.findIndex((tab) => tab.id === tabId);
      let newActiveTabId;

      // If not the first tab, prefer the previous tab
      if (closingIndex > 0) {
        newActiveTabId = tabs[closingIndex - 1].id;
      }
      // If it's the first tab and there are others, select the next one
      else if (updatedTabs.length > 0) {
        newActiveTabId = updatedTabs[0].id;
      }

      if (newActiveTabId) {
        setActiveTabId(newActiveTabId);
        onTabChange?.(newActiveTabId);
      }
    }

    // Dispatch a custom event to notify all components
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("browser-tab-closed", {
          detail: { tabId },
        })
      );
    }

    // Then notify parent for backend operations
    onTabClose?.(tabId);

    // Notify parent of tab reordering
    onTabsReorder?.(updatedTabs);

    // Clear the removing state after a delay
    setTimeout(() => {
      setRemovingTabId(null);
    }, 300);
  };

  // Handler to add a new tab
  const handleAddTab = () => {
    // Use the provided onTabAdd callback if available
    if (onTabAdd) {
      onTabAdd();
      return;
    }

    // Fallback to default behavior if no callback provided
    const newTab: Tab = {
      id: `tab-${Date.now()}`,
      title: `New Tab`,
      content: <div>New Tab Content</div>,
    };

    // Add new tab to the end of the array to maintain visual order with new tabs on the right
    const newTabs = [...tabs, newTab];
    setTabs(newTabs);
    setActiveTabId(newTab.id);
    onTabChange?.(newTab.id);

    // Notify parent of tab reordering
    onTabsReorder?.(newTabs);
  };

  // Drag handlers for tab reordering
  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", tabId);
    setDraggedTabId(tabId);

    // Add a delay to prevent immediately starting drag
    setTimeout(() => {
      const tabEl = document.querySelector(`[data-tab-id="${tabId}"]`);
      if (tabEl) {
        tabEl.classList.add("opacity-50");
      }
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, tabId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTabId(tabId);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    setDragOverTabId(null);
  };

  const handleDrop = (e: React.DragEvent, targetTabId: string) => {
    e.preventDefault();
    const sourceTabId = e.dataTransfer.getData("text/plain");

    // Don't do anything if dropping onto the same tab
    if (sourceTabId === targetTabId) {
      setDraggedTabId(null);
      setDragOverTabId(null);
      return;
    }

    // Find the indexes of the source and target tabs
    const sourceIndex = tabs.findIndex((tab) => tab.id === sourceTabId);
    const targetIndex = tabs.findIndex((tab) => tab.id === targetTabId);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggedTabId(null);
      setDragOverTabId(null);
      return;
    }

    // Create a new array of tabs with the source tab moved to the target position
    const newTabs = [...tabs];
    const [movedTab] = newTabs.splice(sourceIndex, 1);
    newTabs.splice(targetIndex, 0, movedTab);

    // Update the state
    setTabs(newTabs);
    setDraggedTabId(null);
    setDragOverTabId(null);

    // Notify parent of tab reordering
    onTabsReorder?.(newTabs);
  };

  const handleDragEnd = () => {
    setDraggedTabId(null);
    setDragOverTabId(null);

    // Remove opacity class from all tabs
    document.querySelectorAll("[data-tab-id]").forEach((el) => {
      el.classList.remove("opacity-50");
    });
  };

  // Render a message if there are no tabs
  if (tabs.length === 0) {
    return (
      <div className='flex flex-col w-full h-full overflow-hidden rounded-md border bg-background shadow'>
        <div className='flex items-center bg-black border-b'>
          <div className='flex-1'></div>
          <button
            onClick={handleAddTab}
            disabled={isAddingTab}
            className={cn(
              "p-2 mx-2 focus:outline-none shrink-0 transition-colors",
              isAddingTab
                ? "text-muted-foreground cursor-not-allowed opacity-50"
                : "text-muted-foreground hover:text-foreground"
            )}
            aria-label='Add new tab'
            title={isAddingTab ? "Creating new cart..." : "Add new cart"}
          >
            {isAddingTab ? (
              <Loader2 className='animate-spin' size={18} />
            ) : (
              <Plus size={18} />
            )}
          </button>
        </div>
        <div className='flex-1 p-4 flex items-center justify-center'>
          <p className='text-center text-muted-foreground'>
            No carts open. Click the + button to add a new cart.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col w-full h-full overflow-hidden rounded-md border bg-background shadow'>
      {/* Tab header */}
      <div className='flex items-center bg-black border-b'>
        <div
          ref={tabsContainerRef}
          className='flex-1 flex overflow-x-auto scrollbar-hide'
        >
          {tabs.map((tab) => (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              onClick={() => handleTabClick(tab.id)}
              onDoubleClick={() => handleDoubleClick(tab)}
              className={cn(
                "flex items-center px-4 py-2 cursor-pointer border-r relative group",
                "text-sm font-medium transition-colors w-[160px] shrink-0",
                activeTabId === tab.id
                  ? "bg-muted text-foreground"
                  : "bg-black text-muted-foreground hover:bg-muted/10",
                draggedTabId === tab.id ? "opacity-50" : "",
                dragOverTabId === tab.id ? "border-l-2 border-l-primary" : ""
              )}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={(e) => handleDragOver(e, tab.id)}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, tab.id)}
              onDragEnd={handleDragEnd}
            >
              <div className='flex items-center w-full min-w-0'>
                <GripHorizontal
                  size={14}
                  className='mr-2 cursor-grab text-muted-foreground shrink-0'
                />
                {editingTabId === tab.id ? (
                  <input
                    ref={inputRef}
                    type='text'
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onBlur={() => handleTitleSubmit(tab.id)}
                    onKeyDown={(e) => handleKeyDown(e, tab.id)}
                    className='w-full bg-transparent border-none p-0 outline-none focus:ring-0'
                  />
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className='truncate min-w-0 flex-1'>
                          {tab.title}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Double click to rename</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {tabs.length > 1 && (
                  <button
                    className='ml-2 opacity-0 group-hover:opacity-100 hover:text-foreground transition-opacity shrink-0'
                    onClick={(e) => handleTabClose(e, tab.id)}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {activeTabId === tab.id && (
                <div className='absolute bottom-0 left-0 w-full h-0.5 bg-primary' />
              )}
            </div>
          ))}
        </div>

        {/* Add tab button */}
        <button
          onClick={handleAddTab}
          disabled={isAddingTab}
          className={cn(
            "p-2 mx-2 focus:outline-none shrink-0 transition-colors",
            isAddingTab
              ? "text-muted-foreground cursor-not-allowed opacity-50"
              : "text-muted-foreground hover:text-foreground"
          )}
          aria-label='Add new tab'
          title={isAddingTab ? "Creating new cart..." : "Add new cart"}
        >
          {isAddingTab ? (
            <Loader2 className='animate-spin' size={18} />
          ) : (
            <Plus size={18} />
          )}
        </button>
      </div>

      {/* Tab content */}
      <div className='flex-1 overflow-auto'>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={cn(
              "w-full h-full",
              activeTabId === tab.id ? "block" : "hidden"
            )}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BrowserTabs;
